"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useState } from "react";

type ParamValue = string | boolean;

const useUrlParam = <T extends ParamValue>(
  paramName: string,
  defaultValue: T,
) => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [value, setValue] = useState<T>(() => {
    const paramValue = searchParams.get(paramName);
    if (typeof defaultValue === "boolean") {
      return (paramValue === "true") as T;
    }
    return (paramValue ?? defaultValue) as T;
  });

  const updateParam = useCallback(
    (newValue: T | boolean) => {
      setValue(newValue as T);
      const params = new URLSearchParams(searchParams);
      if (newValue === defaultValue) {
        params.delete(paramName);
      } else {
        params.set(paramName, newValue.toString());
      }
      router.push(`?${params.toString()}`, { scroll: false });
    },
    [paramName, defaultValue, searchParams, router],
  );

  return [value, updateParam] as const;
};

export default useUrlParam;
